import React, { useState, useCallback } from 'react';
import { Input, Icon, message } from 'antd';
import { debounce } from 'underscore';
import { getCommunicationsByName } from '../../utils/services/channelAPI';
import styled from 'styled-components';
import REGEX from '../../utils/regex';
import { COMMUNICATION_GENERATOR } from './translationValues';
import { useTranslation } from 'react-i18next';

const NAMESPACE = ['COMMUNICATION_GENERATOR'];

// Custom styled select to replace antd Select
const StyledSelect = styled.select`
  width: 100%;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s;
  background: white;
  cursor: pointer;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;

  &:hover:not(:disabled) {
    border-color: #667eea;
    background-color: #fafbff;
  }

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  &:disabled {
    background: #f1f1f1;
    cursor: not-allowed;
  }
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SelectionSection = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-input-affix-wrapper {
    margin-bottom: 24px;
    gap: 12px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
`;

const SectionIconWrapper = styled.div`
  margin-top: 2px;
  flex-shrink: 0;
  color: #667eea;
  font-size: 20px;
`;

const SectionInfo = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
`;

const SectionDescription = styled.p`
  margin: 4px 0 0 0;
  font-size: 14px;
  color: #666;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateCard = styled.div`
  padding: 20px;
  border: 2px solid ${props => (props.selected ? '#ffeb3b' : '#e0e0e0')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => (props.selected ? '#fffef0' : '#fff')};

  &:hover {
    border-color: ${props => (props.selected ? '#ffeb3b' : '#667eea')};
    background: ${props => (props.selected ? '#fffef0' : '#fafbff')};
  }
`;

const TemplateName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ChannelCard = styled.div`
  padding: 32px 24px;
  border: 2px solid ${props => (props.selected ? '#ffeb3b' : '#e0e0e0')};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => (props.selected ? '#fffef0' : '#fff')};
  text-align: center;

  .channel-icon-wrapper {
    font-size: 32px;
    color: ${props => (props.disabled ? '#b0b0b0' : props.selected ? '#333' : '#666')};
    margin: 0 auto 16px;
  }

  .channel-name {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: ${props => (props.disabled ? '#b0b0b0' : '#1a1a1a')};
  }

  .channel-description {
    margin: 0;
    font-size: 13px;
    color: ${props => (props.disabled ? '#b0b0b0' : '#666')};
  }

  ${props =>
    !props.disabled &&
    `
    &:hover {
      border-color: ${props.selected ? '#ffeb3b' : '#667eea'};
      background: ${props.selected ? '#fffef0' : '#fafbff'};
      cursor: pointer;
    }
  `}
  ${props =>
    props.disabled &&
    `
    border-color: #ece6e6;
    background: #f1f1f1;
      cursor: not-allowed;
    `};
`;

const LanguageSelectContainer = styled.div`
  .ant-select {
    width: 100%;
  }

  .ant-select-selection {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 6px 16px;
    transition: all 0.2s;

    &:hover {
      border-color: #667eea;
      background: #fafbff;
    }
  }

  .ant-select-focused .ant-select-selection {
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const GenerateButton = styled.button`
  width: 100%;
  background: ${props => (props.disabled ? '#e0e0e0' : '#ffeb3b')};
  color: ${props => (props.disabled ? '#999' : '#333')};
  border: none;
  border-radius: 8px;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: #ffeb3b;
  }

  &:active:not(:disabled) {
    background: #ffeb3b;
  }
`;

const EmailSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
const EmailInputContainer = styled.div`
  margin-bottom: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  color: #333;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;

  &.required:after {
    content: ' *';
    color: #f5222d;
    margin-left: 4px;
  }
`;
const FieldHint = styled.p`
  margin: 4px 0 6px 0;
  font-size: 12px;
  color: #999;
`;

const EmailList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const EmailTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;

  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;
const ErrorMessage = styled.div`
  color: #f5222d;
  font-size: 12px;
  margin-top: 4px;
`;

export function TemplateSelector({
  loader,
  communicationDetails,
  filteredCommunications,
  setFilteredCommunications,
  onTemplateChange,
  selectedChannel,
  onChannelChange,
  toAddresses,
  setToAddresses,
  ccAddresses,
  setCcAddresses,
  languages,
  selectedLanguage,
  onLanguageChange,
  onGenerateForm,
}) {
  const [loading, setLoading] = useState(false);

  const [emailInput, setEmailInput] = useState({
    toAddress: '',
    ccAddress: '',
  });
  const [emailErrors, setEmailErrors] = useState({
    toAddress: '',
    ccAddress: '',
  });

  const { t } = useTranslation(NAMESPACE);

  const handleTemplateClick = template => {
    onTemplateChange(template);
  };

  const onCommunicationsSearch = useCallback(async value => {
    if (!value) {
      setFilteredCommunications([]);
      return;
    }
    try {
      setLoading(true);
      const response = await getCommunicationsByName(value);
      if (response?.statusCode === 3210 && response.data) {
        setFilteredCommunications(response.data);
      } else {
        setFilteredCommunications([]);
        message.error(
          t(
            COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.key,
            COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.value,
          ),
        );
      }
    } catch (e) {
      setFilteredCommunications([]);
      message.error(
        t(
          COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.key,
          COMMUNICATION_GENERATOR.LISTING_COMMUNICATIONS_FAILED.value,
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const addEmailAddress = emailType => {
    const inputValue = emailInput[emailType].trim();
    if (!inputValue.length) {
      setEmailErrors(prev => ({
        ...prev,
        [emailType]: t(
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_EMPTY_ERROR.key,
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_EMPTY_ERROR.value,
        ),
      }));
      return;
    }
    if (
      (emailType === 'toAddress' && toAddresses.includes(inputValue)) ||
      (emailType === 'ccAddress' && ccAddresses.includes(inputValue))
    ) {
      setEmailErrors(prev => ({
        ...prev,
        [emailType]: t(
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_EXISTS_ERROR.key,
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_EXISTS_ERROR.value,
        ),
      }));
      return;
    }

    if (!REGEX.EMAIL.test(inputValue)) {
      setEmailErrors(prev => ({
        ...prev,
        [emailType]: t(
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_INVALID_ERROR.key,
          COMMUNICATION_GENERATOR.EMAIL_ADDRESS_INVALID_ERROR.value,
        ),
      }));
      return;
    }

    if (emailType === 'toAddress') {
      setToAddresses(prev => [...prev, inputValue]);
      setEmailInput(prev => ({ ...prev, toAddress: '' }));
    } else {
      setCcAddresses(prev => [...prev, inputValue]);
      setEmailInput(prev => ({ ...prev, ccAddress: '' }));
    }
    setEmailErrors(prev => ({ ...prev, [emailType]: '' }));
  };

  const handleToAddressChange = useCallback(e => {
    const value = e && e.target ? e.target.value : '';
    setEmailInput(prev => ({ ...prev, toAddress: value }));
    setEmailErrors(prev => ({ ...prev, toAddress: '' }));
  }, []);

  const handleCcAddressChange = useCallback(e => {
    const value = e && e.target ? e.target.value : '';
    setEmailInput(prev => ({ ...prev, ccAddress: value }));
    setEmailErrors(prev => ({ ...prev, ccAddress: '' }));
  }, []);

  const debouncedSearch = useCallback(debounce(onCommunicationsSearch, 300), []);

  const isEmail = selectedChannel === 'email';

  const isFormValid =
    communicationDetails &&
    selectedChannel &&
    selectedLanguage &&
    (!isEmail || toAddresses.length > 0);

  const templateLanguages = languages?.filter(lang =>
    communicationDetails?.[selectedChannel]?.includes(lang.code),
  );

  return (
    <SelectorContainer>
      <SelectionSection>
        <SectionHeader>
          <SectionIconWrapper>
            <Icon type="file-text" />
          </SectionIconWrapper>
          <SectionInfo>
            <SectionTitle>
              {t(
                COMMUNICATION_GENERATOR.SELECT_TEMPLATE_LABEL.key,
                COMMUNICATION_GENERATOR.SELECT_TEMPLATE_LABEL.value,
              )}
            </SectionTitle>
            <SectionDescription>
              {t(
                COMMUNICATION_GENERATOR.SELECT_TEMPLATE_DESCRIPTION.key,
                COMMUNICATION_GENERATOR.SELECT_TEMPLATE_DESCRIPTION.value,
              )}
            </SectionDescription>
          </SectionInfo>
        </SectionHeader>
        <Input.Search
          size="large"
          placeholder={`${t(
            COMMUNICATION_GENERATOR.SEARCH_TEMPLATES_PLACEHOLDER_TEXT.key,
            COMMUNICATION_GENERATOR.SEARCH_TEMPLATES_PLACEHOLDER_TEXT.value,
          )}...`}
          onChange={e => debouncedSearch(e.target.value)}
          suffix={loading && <Icon type="loading" />}
        />
        <TemplateGrid>
          {filteredCommunications.slice(0, 4).map(template => (
            <TemplateCard
              key={template.communicationId}
              selected={
                communicationDetails &&
                communicationDetails.communicationId === template.communicationId
              }
              onClick={() => handleTemplateClick(template)}
            >
              <TemplateName>{template.communicationName}</TemplateName>
            </TemplateCard>
          ))}
          {filteredCommunications.length === 0 && communicationDetails?.communicationId && (
            <TemplateCard selected={true}>
              <TemplateName>{communicationDetails?.communicationName}</TemplateName>
            </TemplateCard>
          )}
        </TemplateGrid>
      </SelectionSection>

      <SelectionSection>
        <SectionHeader>
          <SectionIconWrapper>
            <Icon type="mail" />
          </SectionIconWrapper>
          <SectionInfo>
            <SectionTitle>
              {t(
                COMMUNICATION_GENERATOR.SELECT_CHANNEL_LABEL.key,
                COMMUNICATION_GENERATOR.SELECT_CHANNEL_LABEL.value,
              )}
            </SectionTitle>
            <SectionDescription>
              {t(
                COMMUNICATION_GENERATOR.SELECT_CHANNEL_DESCRIPTION.key,
                COMMUNICATION_GENERATOR.SELECT_CHANNEL_DESCRIPTION.value,
              )}
            </SectionDescription>
          </SectionInfo>
        </SectionHeader>

        <ChannelGrid>
          <ChannelCard
            selected={selectedChannel === 'print'}
            onClick={() => communicationDetails?.print?.length && onChannelChange('print')}
            disabled={!communicationDetails?.print?.length}
          >
            <div className="channel-icon-wrapper">
              <Icon type="printer" />
            </div>
            <h3 className="channel-name">
              {t(
                COMMUNICATION_GENERATOR.PRINT_OPTION.key,
                COMMUNICATION_GENERATOR.PRINT_OPTION.value,
              )}
            </h3>
            <p className="channel-description">
              {t(
                COMMUNICATION_GENERATOR.PRINT_OPTION_DESCRIPTION.key,
                COMMUNICATION_GENERATOR.PRINT_OPTION_DESCRIPTION.value,
              )}
            </p>
          </ChannelCard>

          <ChannelCard
            selected={selectedChannel === 'email'}
            onClick={() => communicationDetails?.email?.length && onChannelChange('email')}
            disabled={!communicationDetails?.email?.length}
          >
            <div className="channel-icon-wrapper">
              <Icon type="mail" />
            </div>
            <h3 className="channel-name">
              {t(
                COMMUNICATION_GENERATOR.EMAIL_OPTION.key,
                COMMUNICATION_GENERATOR.EMAIL_OPTION.value,
              )}
            </h3>
            <p className="channel-description">
              {t(
                COMMUNICATION_GENERATOR.EMAIL_OPTION_DESCRIPTION.key,
                COMMUNICATION_GENERATOR.EMAIL_OPTION_DESCRIPTION.value,
              )}
            </p>
          </ChannelCard>
        </ChannelGrid>

        {selectedChannel === 'email' && (
          <EmailSection>
            {/* To Email Addresses */}
            <EmailInputContainer>
              <FieldLabel className="required">
                {t(
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_LABEL.key,
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_LABEL.value,
                )}
              </FieldLabel>
              <FieldHint>
                {t(
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_DESCRIPTION.key,
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_DESCRIPTION.value,
                )}
              </FieldHint>
              {toAddresses.length > 0 && (
                <EmailList>
                  {toAddresses.map(email => (
                    <EmailTag key={email}>
                      {email}
                      <button
                        onClick={() => setToAddresses(prev => prev.filter(id => id !== email))}
                      >
                        ×
                      </button>
                    </EmailTag>
                  ))}
                </EmailList>
              )}
              <Input
                type="email"
                size="large"
                placeholder={t(
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_PLACEHOLDER_TEXT.key,
                  COMMUNICATION_GENERATOR.TO_EMAIL_ADDRESS_PLACEHOLDER_TEXT.value,
                )}
                value={emailInput.toAddress || ''}
                onChange={handleToAddressChange}
                className={emailErrors.toAddress ? 'error' : ''}
                onPressEnter={() => addEmailAddress('toAddress')}
              />
              {emailErrors.toAddress && <ErrorMessage>{emailErrors.toAddress}</ErrorMessage>}
            </EmailInputContainer>

            {/* CC Email Addresses */}
            <EmailInputContainer>
              <FieldLabel>
                {t(
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_LABEL.key,
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_LABEL.value,
                )}
              </FieldLabel>
              <FieldHint>
                {t(
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_DESCRIPTION.key,
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_DESCRIPTION.value,
                )}
              </FieldHint>
              {ccAddresses.length > 0 && (
                <EmailList>
                  {ccAddresses.map(email => (
                    <EmailTag key={email}>
                      {email}
                      <button
                        onClick={() => setCcAddresses(prev => prev.filter(id => id !== email))}
                      >
                        ×
                      </button>
                    </EmailTag>
                  ))}
                </EmailList>
              )}
              <Input
                type="email"
                size="large"
                placeholder={t(
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_PLACEHOLDER_TEXT.key,
                  COMMUNICATION_GENERATOR.CC_EMAIL_ADDRESS_PLACEHOLDER_TEXT.value,
                )}
                value={emailInput.ccAddress || ''}
                onChange={handleCcAddressChange}
                className={emailErrors.ccAddress ? 'error' : ''}
                onPressEnter={() => addEmailAddress('ccAddress')}
              />
              {emailErrors.ccAddress && <ErrorMessage>{emailErrors.ccAddress}</ErrorMessage>}
            </EmailInputContainer>
          </EmailSection>
        )}
      </SelectionSection>

      {/* Language Selection Section */}
      <SelectionSection>
        <SectionHeader>
          <SectionIconWrapper>
            <Icon type="global" />
          </SectionIconWrapper>
          <SectionInfo>
            <SectionTitle>
              {t(
                COMMUNICATION_GENERATOR.SELECT_LANGUAGE_LABEL.key,
                COMMUNICATION_GENERATOR.SELECT_LANGUAGE_LABEL.value,
              )}
            </SectionTitle>
            <SectionDescription>
              {t(
                COMMUNICATION_GENERATOR.SELECT_LANGUAGE_DESCRIPTION.key,
                COMMUNICATION_GENERATOR.SELECT_LANGUAGE_DESCRIPTION.value,
              )}
            </SectionDescription>
          </SectionInfo>
        </SectionHeader>

        <LanguageSelectContainer>
          <StyledSelect
            value={selectedLanguage || ''}
            onChange={e => onLanguageChange(e.target.value)}
            disabled={!templateLanguages.length}
          >
            <option value="">{`${t(
              COMMUNICATION_GENERATOR.SELECT_LANGUAGE_PLACEHOLDER_TEXT.key,
              COMMUNICATION_GENERATOR.SELECT_LANGUAGE_PLACEHOLDER_TEXT.value,
            )}...`}</option>
            {templateLanguages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </StyledSelect>
        </LanguageSelectContainer>
      </SelectionSection>

      {/* Generate Button */}
      <GenerateButton disabled={!isFormValid} onClick={onGenerateForm}>
        {loader && <Icon type="loading" />}{' '}
        {t(
          COMMUNICATION_GENERATOR.GENERATE_FORM_BUTTON.key,
          COMMUNICATION_GENERATOR.GENERATE_FORM_BUTTON.value,
        )}
      </GenerateButton>
    </SelectorContainer>
  );
}
